'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { LETTER_SECTIONS, LETTER_NAMES } from '../data/letterData';
import type { AnnotatedPhrase, LetterSection } from '../data/letterData';
import NameHighlight from './NameHighlight';

import { KhalifahSLTerm, FounderThreadVoice, ThreeTasksCallout, QuranFirstAnnotation } from './CaretakerEnhancements';

import { HandedALensEmergence, PassageAnnotationsPanel, FiveSectionTwoDemands } from './PlotTwoAuditEnhancements';

import { GuidedPathTrail } from '@/app/open-letter/components/GuidedPathTrail';

import {
  FullInteractiveMural,
  FourBeatLensQuickMode,
  ObservationChainTapestry,
  GarmentWeavingProcess,
  UboorReturnThreshold,
} from './Plot3MuralEnhancements';
import ExploreFurther from './ExploreFurther';
import BranchButton from './BranchButton';



// ─── SECTION BACKGROUND COLORS ────────────────────────────────────────────────
const VARIANT_BG: Record<string, string> = {
  immersive: '#061008',
  split: '#061a14',
  centered: '#f5f0e8',
  dark: '#060d06',
  terminal: '#030806',
  'paired-names': '#060d1a',
  'golden-warm': '#1a0e06',
  quote: '#060d12',
};

const VARIANT_TEXT: Record<string, string> = {
  immersive: '#d4c5a9',
  split: '#d4c5a9',
  centered: '#1a1a0e',
  dark: '#d4c5a9',
  terminal: '#d4c5a9',
  'paired-names': '#d4c5a9',
  'golden-warm': '#d4c5a9',
  quote: '#d4c5a9',
};

// ─── ANNOTATED PHRASE ─────────────────────────────────────────────────────────
function AnnotatedPhraseComp({ phrase, connection, href, nameId }: AnnotatedPhrase) {
  const [open, setOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  if (nameId) {
    return <NameHighlight nameId={nameId} displayText={phrase} />;
  }
  return (
    <>
      <span
        style={{
          background: open ? 'rgba(201,168,76,0.15)' : 'rgba(201,168,76,0.08)',
          borderBottom: '1px solid rgba(201,168,76,0.4)',
          cursor: 'pointer',
          transition: 'background 0.2s',
          position: 'relative',
          display: 'inline',
          padding: '0 2px',
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => { setPanelOpen(true); setOpen(false); }}
        onTouchStart={() => { setPanelOpen(true); }}
      >
        {phrase}
        {open && (
          <span style={{
            position: 'fixed',
            bottom: 'auto',
            left: '50%',
            transform: 'translateX(-50%)',
            top: '50%',
            marginTop: '-40px',
            background: 'rgba(6,13,18,0.97)',
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: 4,
            padding: '8px 12px',
            maxWidth: 'min(280px, 80vw)',
            width: 'max-content',
            zIndex: 50,
            fontSize: 11,
            color: '#d4c5a9',
            lineHeight: 1.4,
            pointerEvents: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}>
            <span style={{ color: '#c9a84c', display: 'block', marginBottom: 3, fontSize: 10, letterSpacing: '.06em' }}>{connection}</span>
            <span style={{ color: '#2d8a87', fontSize: 10 }}>↗ Tap to open panel</span>
          </span>
        )}
      </span>
      {panelOpen && (
        <>
          {/* Backdrop */}
          <span
            onClick={() => setPanelOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(6,13,18,0.6)',
              zIndex: 200,
              backdropFilter: 'blur(2px)',
              display: 'block',
            } as React.CSSProperties}
          />
          {/* Panel — full-width on mobile */}
          <span style={{
            position: 'fixed',
            right: 0,
            top: 0,
            bottom: 0,
            width: 'min(420px, 100vw)',
            background: '#060d12',
            borderLeft: '1px solid rgba(201,168,76,0.25)',
            zIndex: 201,
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideInRight 0.32s cubic-bezier(0.22,1,0.36,1)',
            overflowX: 'hidden',
          } as React.CSSProperties}>
            <style>{`@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
            {/* Header */}
            <span style={{ padding: '20px', borderBottom: '1px solid rgba(201,168,76,0.15)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 } as React.CSSProperties}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 9, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>PLATFORM CONNECTION</span>
                <span style={{ display: 'block', fontSize: 13, color: '#d4c5a9', lineHeight: 1.5, wordBreak: 'break-word' }}>{connection}</span>
              </span>
              <button
                onClick={() => setPanelOpen(false)}
                style={{ background: 'none', border: '1px solid rgba(201,168,76,0.2)', color: 'rgba(201,168,76,0.6)', cursor: 'pointer', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, marginLeft: 12, borderRadius: 4, touchAction: 'manipulation' }}
                aria-label="Close panel"
              >×</button>
            </span>
            {/* Content */}
            <span style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 } as React.CSSProperties}>
              <span style={{ display: 'block', fontSize: 11, color: '#9a8f7d', lineHeight: 1.7 }}>
                This concept is developed further in the platform. The phrase <em style={{ color: '#c9a84c' }}>"{phrase}"</em> connects to the following section of the project.
              </span>
              {/* Divider */}
              <span style={{ display: 'block', height: 1, background: 'rgba(201,168,76,0.1)' }} />
              {/* Explore Further CTA */}
              <span style={{ display: 'block', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, padding: '16px' }}>
                <span style={{ display: 'block', fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>⚠ Explore Further</span>
                <span style={{ display: 'block', fontSize: 11, color: '#9a8f7d', lineHeight: 1.6, marginBottom: 12 }}>
                  Following this link will take you away from the letter and interrupt your reading. You can return here at any time.
                </span>
                <Link
                  href={href}
                  style={{ display: 'block', textAlign: 'center', padding: '12px 16px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontSize: 11, letterSpacing: '.08em', textDecoration: 'none', borderRadius: 4, touchAction: 'manipulation' }}
                >
                  → See in Platform
                </Link>
              </span>
            </span>
          </span>
        </>
      )}
    </>
  );
}

// ─── RENDER PARAGRAPH WITH ANNOTATIONS ───────────────────────────────────────
function renderAnnotatedParagraph(text: string, phrases: AnnotatedPhrase[]) {
  let result: React.ReactNode[] = [text];
  phrases.forEach((ap, pi) => {
    result = result.flatMap((node, ni) => {
      if (typeof node !== 'string') return [node];
      const idx = node.indexOf(ap.phrase);
      if (idx === -1) return [node];
      return [
        node.slice(0, idx),
        <AnnotatedPhraseComp key={`${pi}-${ni}`} {...ap} />,
        node.slice(idx + ap.phrase.length),
      ];
    });
  });
  return result;
}

// ─── AYAH BLOCK ───────────────────────────────────────────────────────────────
function AyahBlock({ ref: refText, text, variant }: { ref: string; text: string; variant?: string }) {
  const isLight = variant === 'centered';
  return (
    <blockquote style={{
      borderLeft: `3px solid rgba(201,168,76,${isLight ? '0.6' : '0.4'})`,
      paddingLeft: 20,
      margin: '20px 0',
      background: isLight ? 'rgba(201,168,76,0.04)' : 'rgba(201,168,76,0.04)',
      borderRadius: '0 4px 4px 0',
      padding: '16px 20px',
    }}>
      <p style={{
        fontFamily: "'Georgia', serif",
        fontStyle: 'italic',
        fontSize: 14,
        color: isLight ? '#3a2e1a' : '#d4c5a9',
        lineHeight: 1.8,
        marginBottom: 8,
      }}>{text}</p>
      <span style={{ fontSize: 10, color: '#c9a84c', letterSpacing: '.08em' }}>{refText}</span>
    </blockquote>
  );
}

// ─── HADITH BLOCK ─────────────────────────────────────────────────────────────
function HadithBlock({ narrator, text, grade, source, variant }: { narrator: string; text: string; grade: string; source: string; variant?: string }) {
  const isLight = variant === 'centered';
  return (
    <div style={{
      borderLeft: '3px solid rgba(45,138,135,0.5)',
      paddingLeft: 20,
      margin: '20px 0',
      background: isLight ? 'rgba(45,138,135,0.04)' : 'rgba(45,138,135,0.06)',
      borderRadius: '0 4px 4px 0',
      padding: '16px 20px',
    }}>
      <div style={{ fontSize: 9, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
        {narrator}
      </div>
      <p style={{
        fontFamily: "'Georgia', serif",
        fontStyle: 'italic',
        fontSize: 14,
        color: isLight ? '#3a2e1a' : '#d4c5a9',
        lineHeight: 1.8,
        marginBottom: 8,
      }}>{text}</p>
      <div style={{ fontSize: 9, color: '#7a6f5d' }}>
        <span style={{ color: '#2d8a87' }}>{grade}</span> — {source}
      </div>
    </div>
  );
}

// ─── ROOT DISPLAY BLOCK ───────────────────────────────────────────────────────
function RootBlock({ arabicRoot, rootLetters, rootMeanings, variant }: { arabicRoot: string; rootLetters?: string; rootMeanings?: string[]; variant: string }) {
  const isLight = variant === 'centered';
  return (
    <div style={{ marginBottom: 32 }}>
      {/* Large Arabic root letters */}
      <div className="letter-root-arabic" style={{
        fontFamily: "'Amiri', serif",
        fontSize: 48,
        color: isLight ? 'rgba(139,100,20,0.3)' : 'rgba(201,168,76,0.3)',
        textAlign: 'center',
        letterSpacing: 16,
        marginBottom: 8,
        lineHeight: 1,
        wordBreak: 'break-word',
      }}>{arabicRoot}</div>
      {rootLetters && (
        <div style={{ textAlign: 'center', fontSize: 11, color: '#c9a84c', letterSpacing: '.12em', marginBottom: 16 }}>
          {rootLetters}
        </div>
      )}
      {rootMeanings && rootMeanings.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          justifyContent: 'center',
          marginBottom: 8,
        }}>
          {rootMeanings.map((m, i) => (
            <span key={i} style={{
              fontSize: 11,
              color: isLight ? '#5a4a2a' : '#9a8f7d',
              background: isLight ? 'rgba(139,100,20,0.06)' : 'rgba(201,168,76,0.05)',
              border: `1px solid ${isLight ? 'rgba(139,100,20,0.15)' : 'rgba(201,168,76,0.1)'}`,
              borderRadius: 3,
              padding: '3px 8px',
            }}>{m}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ANNOTATIONS PANEL ────────────────────────────────────────────────────────
function AnnotationsPanel({ section }: { section: LetterSection }) {
  const namesInSection = section.namesExtracted.map(id => LETTER_NAMES[id]).filter(Boolean);
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="letter-annotation-panel" style={{ padding: 'clamp(24px, 4vw, 48px) clamp(12px, 2vw, 20px) clamp(24px, 4vw, 48px) clamp(12px, 2vw, 20px)', position: 'sticky', top: 80, alignSelf: 'start' }}>
      {namesInSection.length > 0 && (
        <div style={{
          background: 'rgba(6,13,18,0.6)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: 6,
          padding: 16,
          marginBottom: 12,
        }}>
          <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
            NAMES IN THIS SECTION
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {namesInSection.map((n, i) => (
              <Link key={i} href="/names-reference" style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '5px 8px',
                background: 'rgba(201,168,76,0.05)',
                border: '1px solid rgba(201,168,76,0.15)',
                borderRadius: 4, cursor: 'pointer', textDecoration: 'none',
              }}>
                <span style={{ fontSize: 14, color: '#c9a84c', fontFamily: "'Amiri', serif" }}>{n.arabic}</span>
                <span style={{ fontSize: 8, color: '#7a6f5d', marginTop: 2 }}>{n.transliteration}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div style={{
        background: 'rgba(26,74,46,0.1)',
        border: '1px solid rgba(45,122,71,0.2)',
        borderRadius: 6,
        padding: 16,
        marginBottom: 12,
      }}>
        <div style={{ fontSize: 7, color: '#2d7a47', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
          THIS SECTION GROUNDS
        </div>
        {section.platformBridges.map((b, i) => (
          <Link key={i} href={b.href} style={{
            display: 'block', padding: '6px 0',
            borderBottom: i < section.platformBridges.length - 1 ? '1px solid rgba(45,122,71,0.1)' : 'none',
            textDecoration: 'none',
          }}>
            <div style={{ fontSize: 10, color: '#2d7a47' }}>→ {b.label}</div>
            <div style={{ fontSize: 9, color: '#7a6f5d', marginTop: 2, lineHeight: 1.4 }}>{b.description}</div>
          </Link>
        ))}
      </div>
      {section.reflectionQuestions && section.reflectionQuestions.length > 0 && (
        <div style={{
          background: 'rgba(6,13,18,0.6)',
          border: '1px solid rgba(201,168,76,0.1)',
          borderLeft: '3px solid rgba(201,168,76,0.4)',
          borderRadius: '0 6px 6px 0',
          padding: 16,
        }}>
          <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
            REFLECTION QUESTIONS
          </div>
          {section.reflectionQuestions.map((q, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              {q.role && <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.08em', marginBottom: 3 }}>{q.role.toUpperCase()}</div>}
              <div style={{ fontSize: 10, color: '#9a8f7d', lineHeight: 1.6 }}>{q.question}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── POEM DISPLAY ─────────────────────────────────────────────────────────────
function PoemDisplay({ poem }: { poem: NonNullable<LetterSection['poem']> }) {
  const [visible, setVisible] = useState<boolean[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          poem.lines.forEach((_, i) => {
            setTimeout(() => {
              setVisible(prev => { const next = [...prev]; next[i] = true; return next; });
            }, i * 100);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [poem.lines]);

  const styleMap = {
    gold: '#c9a84c',
    goldLt: 'rgba(201,168,76,0.7)',
    cream: '#d4c5a9',
    muted: 'rgba(212,197,169,0.4)',
  };

  return (
    <div ref={ref} style={{ padding: 'clamp(24px, 5vw, 48px) 0', textAlign: 'center', overflowX: 'hidden' }}>
      {poem.lines.map((line, i) => (
        <div
          key={i}
          className={line.size > 28 ? 'letter-poem-line-lg' : ''}
          style={{
            fontFamily: "'Amiri', serif",
            fontStyle: 'italic',
            fontSize: line.size,
            color: styleMap[line.style],
            marginBottom: line.size > 28 ? 16 : 8,
            opacity: visible[i] ? 1 : 0,
            transform: visible[i] ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
          }}
        >
          {line.text}
        </div>
      ))}
    </div>
  );
}

// ─── SPECIAL CALLOUT ─────────────────────────────────────────────────────────
function SpecialCallout({ callout, variant }: { callout: NonNullable<LetterSection['specialCallout']>; variant: string }) {
  const isTerminal = variant === 'terminal';
  return (
    <div style={{
      background: isTerminal ? 'rgba(45,122,71,0.1)' : 'rgba(201,168,76,0.04)',
      border: `1px solid ${isTerminal ? 'rgba(45,122,71,0.3)' : 'rgba(201,168,76,0.2)'}`,
      borderRadius: 6,
      padding: 'clamp(14px, 3vw, 20px) clamp(12px, 3vw, 24px)',
      marginTop: 24,
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}>
      <div style={{ fontSize: 'clamp(9px, 2.5vw, 11px)', color: isTerminal ? '#2d7a47' : '#c9a84c', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10, textAlign: 'center', overflowWrap: 'break-word', wordBreak: 'break-word', lineHeight: 1.5 }}>
        {callout.title}
      </div>
      <p style={{ fontSize: 12, color: '#9a8f7d', lineHeight: 1.6, textAlign: 'center', fontStyle: 'italic', overflowWrap: 'break-word' }}>{callout.body}</p>
      {callout.link && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Link href={callout.link} style={{ fontSize: 11, color: '#c9a84c', textDecoration: 'none' }}>{callout.linkText}</Link>
        </div>
      )}
    </div>
  );
}

// ─── CONVERGENCE DISPLAY ──────────────────────────────────────────────────────
function ConvergenceDisplay() {
  const branches = [
    { label: 'Keys to Kulli Haal', note: 'Named in the letter', href: '/keys-to-kulli-haal', color: '#c9a84c' },
    { label: 'Inner Reality Lens', note: 'Named and described in the letter', href: '/inner-reality-lens', color: '#c9a84c' },
    { label: 'Muslim Resource Guide', note: 'The Caretaker role — the letter\'s purpose', href: '/muslim-resource-guide', color: '#2d7a47' },
    { label: 'Ayat in Creation', note: 'Signs surround us', href: '/ayat-in-creation', color: '#2d8a87' },
    { label: 'Names Reference', note: '50+ Names extracted from the letter', href: '/names-reference', color: '#c9a84c' },
    { label: 'Bridge Pages', note: 'Uboor Bridge-Quests — named in the letter', href: '/bridge-irl-application', color: '#2d8a87' },
    { label: 'ObsPanel', note: 'Ilm Dock — named in the letter', href: '/inner-reality-lens', color: '#2d7a47' },
  ];
  return (
    <div style={{
      background: 'rgba(201,168,76,0.03)',
      borderTop: '1px solid rgba(201,168,76,0.1)',
      padding: '48px 24px',
      textAlign: 'center',
    }}>
      <div style={{ fontFamily: "'Amiri', serif", fontSize: 32, color: '#c9a84c', marginBottom: 8 }}>
        بسم الله الرحمن الرحيم
      </div>
      <div style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 32 }}>
        ALL PLATFORM COMPONENTS TRACE BACK TO THIS LETTER
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        maxWidth: 900,
        margin: '0 auto',
      }}>
        {branches.map((b, i) => (
          <Link key={i} href={b.href} style={{
            display: 'block',
            padding: '16px',
            background: 'rgba(6,13,18,0.6)',
            border: `1px solid ${b.color}22`,
            borderRadius: 6,
            textDecoration: 'none',
            transition: 'all 0.2s',
          }}>
            <div style={{ fontSize: 12, color: b.color, marginBottom: 4 }}>→ {b.label}</div>
            <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.4 }}>{b.note}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
// ─── HAWAS SENSES DISPLAY ─────────────────────────────────────────────────────
function HawasSenses() {
  const senses = [
    { emoji: '👁', arabic: 'بَصَر', transliteration: 'Basar', label: 'Sight' },
    { emoji: '👂', arabic: 'سَمْع', transliteration: 'Sama', label: 'Sound' },
    { emoji: '🌿', arabic: 'شَمّ', transliteration: 'Shamm', label: 'Scent' },
    { emoji: '◉', arabic: 'ذَوْق', transliteration: 'Dhawq', label: 'Taste' },
    { emoji: '✋', arabic: 'لَمْس', transliteration: 'Lams', label: 'Touch' },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', margin: '24px 0' }}>
      {senses.map((s, i) => (
        <div key={i} style={{
          background: 'rgba(45,122,71,0.08)',
          border: '1px solid rgba(45,122,71,0.2)',
          borderRadius: 8,
          padding: '16px 20px',
          textAlign: 'center',
          minWidth: 100,
        }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>{s.emoji}</div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#c9a84c', marginBottom: 4 }}>{s.arabic}</div>
          <div style={{ fontSize: 10, color: '#d4c5a9', marginBottom: 2 }}>{s.transliteration}</div>
          <div style={{ fontSize: 9, color: '#7a6f5d' }}>{s.label}</div>
          <div style={{ fontSize: 8, color: '#2d7a47', marginTop: 6 }}>Used in: IRL Lens ObsPanel</div>
        </div>
      ))}
    </div>
  );
}

// ─── AL-QADIR ROOT FISSION PANEL ─────────────────────────────────────────────
function AlQadirRootFissionPanel() {
  const [activeTab, setActiveTab] = React.useState<'field' | 'fission' | 'encounter'>('field');
  const [selectedDimension, setSelectedDimension] = React.useState<number | null>(null);

  const rootDimensions = [
    { id: 0, cluster: 'Measurement', arabic: 'قَدَرَ', meaning: 'To measure something', depth: 'Precision — the leaf\'s exact dimensions are measured. Nothing is approximate in creation.', color: '#c9a84c' },
    { id: 1, cluster: 'Measurement', arabic: 'قَدَّرَ', meaning: 'To make manifest the measure of something', depth: 'The measurement becomes visible — the leaf\'s size, shape, and color are the manifest measure.', color: '#c9a84c' },
    { id: 2, cluster: 'Measurement', arabic: 'قَدَّرَ', meaning: 'To distribute or apportion (as though by measure)', depth: 'Provision is apportioned — each organism receives exactly its measured share.', color: '#c9a84c' },
    { id: 3, cluster: 'Decree', arabic: 'قَدَر', meaning: 'Measuring out, Decree', depth: 'The Qadr — the written decree that precedes all manifestation. The leaf was decreed before the tree.', color: '#9a7a3a' },
    { id: 4, cluster: 'Decree', arabic: 'قَدَّرَ', meaning: 'To ordain', depth: 'Ordained — not merely permitted but actively decreed. The leaf\'s existence is an act of divine will.', color: '#9a7a3a' },
    { id: 5, cluster: 'Decree', arabic: 'قَدَّرَ', meaning: 'To decide a matter (as though measuring to fit)', depth: 'Decision as measurement — the decree prevails over all resistance. Nothing can unmeasure what was measured.', color: '#9a7a3a' },
    { id: 6, cluster: 'Intellect', arabic: 'قَدَّرَ', meaning: 'To exercise thought in preparing something', depth: 'The leaf\'s DNA blueprint is a prepared plan — thought exercised before the leaf existed.', color: '#2d8a87' },
    { id: 7, cluster: 'Intellect', arabic: 'قَدَّرَ', meaning: 'To make use of reason', depth: 'Reason operative in creation — the photosynthesis process is a reasoned system, not random chemistry.', color: '#2d8a87' },
    { id: 8, cluster: 'Power', arabic: 'قَدَّرَ', meaning: 'To have the power, To accomplish', depth: 'Power that accomplishes — not potential power but active, completing power.', color: '#7a4a9a' },
    { id: 9, cluster: 'Power', arabic: 'قَدَّرَ', meaning: 'To be rendered able, To be able', depth: 'The leaf is rendered able to photosynthesize — its capacity is given, not inherent.', color: '#7a4a9a' },
    { id: 10, cluster: 'Power', arabic: 'قَدَّرَ', meaning: 'To prevail, To be empowered', depth: 'Prevailing power — the decree prevails over all resistance. Nothing can unmeasure what was measured.', color: '#7a4a9a' },
    { id: 11, cluster: 'Power', arabic: 'قَدَّرَ', meaning: 'To have control over', depth: 'Complete control — not partial governance but total sovereignty over every measurement given.', color: '#7a4a9a' },
  ];

  const clusters = ['Measurement', 'Decree', 'Intellect', 'Power'];
  const clusterColors: Record<string, string> = { Measurement: '#c9a84c', Decree: '#9a7a3a', Intellect: '#2d8a87', Power: '#7a4a9a' };

  return (
    <div style={{ margin: '32px 0', borderRadius: 10, border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(6,13,18,0.6)', overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(201,168,76,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 4 }}>◈ Root Meaning Field · ق - د - ر</div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl' }}>الْقَادِر</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['field', 'fission', 'encounter'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '5px 12px', borderRadius: 4, border: `1px solid ${activeTab === tab ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.15)'}`, background: activeTab === tab ? 'rgba(201,168,76,0.1)' : 'transparent', color: activeTab === tab ? '#c9a84c' : 'rgba(212,197,169,0.4)', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'monospace' }}>
              {tab === 'field' ? 'Meaning Field' : tab === 'fission' ? 'Root Fission' : 'Encounter'}
            </button>
          ))}
        </div>
      </div>
      {activeTab === 'field' && (
        <div style={{ padding: '20px' }}>
          <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.55)', lineHeight: 1.7, marginBottom: 16 }}>Al-Qadir is not a single meaning — it is a meaning field. Twelve dimensions, four clusters. The root moves from precision (measuring) through authority (decreeing) through intellect (reasoning) to capacity (empowering). Select any dimension to open its depth.</p>
          {clusters.map(cluster => (
            <div key={cluster} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, color: clusterColors[cluster], letterSpacing: '.12em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 8 }}>◆ {cluster}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {rootDimensions.filter(d => d.cluster === cluster).map(dim => (
                  <button key={dim.id} onClick={() => setSelectedDimension(selectedDimension === dim.id ? null : dim.id)} style={{ padding: '6px 10px', borderRadius: 5, border: `1px solid ${selectedDimension === dim.id ? dim.color : `${dim.color}44`}`, background: selectedDimension === dim.id ? `${dim.color}18` : 'transparent', color: selectedDimension === dim.id ? dim.color : 'rgba(212,197,169,0.65)', fontSize: 11, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>{dim.meaning}</button>
                ))}
              </div>
              {selectedDimension !== null && rootDimensions[selectedDimension]?.cluster === cluster && (
                <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 6, background: `${clusterColors[cluster]}0d`, border: `1px solid ${clusterColors[cluster]}33`, borderLeft: `3px solid ${clusterColors[cluster]}` }}>
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: clusterColors[cluster], marginBottom: 6, direction: 'rtl' }}>{rootDimensions[selectedDimension].arabic}</div>
                  <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.75)', lineHeight: 1.7 }}>{rootDimensions[selectedDimension].depth}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {activeTab === 'fission' && (
        <div style={{ padding: '20px' }}>
          <div style={{ padding: '12px 16px', borderRadius: 6, background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)', marginBottom: 16 }}>
            <div style={{ fontSize: 9, color: 'rgba(45,138,135,0.8)', letterSpacing: '.12em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 6 }}>Root Fission — Second-Order Depth Layer</div>
            <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.75 }}>After extracting a Name in the IRL Lens, crack open its three-letter root. Not all twelve dimensions of ق-د-ر manifest in every encounter. The leaf shows "measure" and "decree" but not "prevail." Identify which dimensions are active in your specific encounter.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rootDimensions.map(dim => (
              <div key={dim.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 5, background: 'rgba(6,13,18,0.4)', border: '1px solid rgba(201,168,76,0.08)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: dim.color, flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.8)', marginBottom: 3 }}>{dim.meaning}</div>
                  <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.45)', lineHeight: 1.5 }}>{dim.depth}</div>
                </div>
                <div style={{ fontSize: 9, color: dim.color, fontFamily: 'monospace', flexShrink: 0 }}>{dim.cluster}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'encounter' && (
        <div style={{ padding: '20px' }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.12em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 12 }}>Root Fission Applied to Four Encounters</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { encounter: 'A Leaf', active: ['To measure something', 'To make manifest the measure', 'To exercise thought in preparing', 'To be rendered able'], note: 'Precise dimensions, prepared blueprint, rendered able to photosynthesize' },
              { encounter: 'A Community Disparity', active: ['To distribute or apportion', 'To exercise thought in preparing', 'To decide a matter'], note: 'Apportionment of provision, thought in the design of systems' },
              { encounter: 'A Seed Germinating', active: ['To ordain', 'To have the power', 'To accomplish'], note: 'Ordained to grow, power that accomplishes the decree' },
              { encounter: 'A Watershed', active: ['To prevail', 'To have control over', 'To make manifest the measure'], note: 'Prevailing flow, complete control over the water cycle' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 6, background: 'rgba(6,13,18,0.5)', border: '1px solid rgba(201,168,76,0.12)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#c9a84c', flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: '#c9a84c', fontFamily: 'monospace', marginBottom: 8 }}>◆ {item.encounter}</div>
                  {item.active.map((dim, j) => (
                    <div key={j} style={{ fontSize: 10, color: 'rgba(212,197,169,0.7)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                      <span style={{ color: '#c9a84c', fontSize: 8 }}>✓</span> {dim}
                    </div>
                  ))}
                  <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)', lineHeight: 1.5, fontStyle: 'italic', marginTop: 6 }}>{item.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AL-QADIR COSMOLOGICAL ANCHOR ────────────────────────────────────────────
function AlQadirCosmologicalAnchor() {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ margin: '28px 0', borderRadius: 8, border: '1px solid rgba(138,138,184,0.25)', background: 'rgba(138,138,184,0.04)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(v => !v)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 9, color: 'rgba(138,138,184,0.8)', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace' }}>◈ Pen Hadith · Cosmological Anchor · Observation Chain Language</span>
        <span style={{ fontSize: 16, color: 'rgba(138,138,184,0.6)', fontFamily: 'monospace' }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ height: 1, background: 'rgba(138,138,184,0.15)', marginBottom: 4 }} />
          <p style={{ fontSize: 13, color: 'rgba(212,197,169,0.75)', lineHeight: 1.85 }}>The Pen Hadith is the cosmological anchor of the IRL Lens methodology. Everything was already written — decreed — before creation began. The garden does not generate new information. It uncovers what was already decreed. Every observation the caretaker submits is a recognition of what was always there.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Observation Chain Archive', note: 'A communal tafsir of the Qadr — each observation is a reading of what was already written. The Archive grows not because new information enters creation but because more caretakers are reading what was always written.', color: 'rgba(45,122,71,0.6)' },
              { label: 'IRL Lens Language', note: '"You recognized Al-Muhyi here" — not "You discovered." The recognition is yours. The reality was always there, active since the Pen recorded it.', color: 'rgba(201,168,76,0.6)' },
              { label: 'Niyyah Engine', note: '"Ya Allah, help me see Your Names in this" — the deepest Niyyah is a Du\'a aligned with Hadith 19: "If you ask, then ask Allah alone."', color: 'rgba(138,138,184,0.6)' },
              { label: 'Garden Purpose', note: 'The garden is a response tool, not a creation tool. The caretaker acts within a decree they did not write and cannot alter. Their agency is in how they respond to what was already written.', color: 'rgba(201,168,76,0.4)' },
            ].map((item, i) => (
              <div key={i} style={{ padding: '10px 12px', borderRadius: 6, background: 'rgba(6,13,18,0.5)', border: `1px solid ${item.color}` }}>
                <div style={{ fontSize: 9, color: item.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5, fontFamily: 'monospace', }}>◆ {item.label}</div>
                <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.6 }}>{item.note}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderLeft: '3px solid rgba(201,168,76,0.4)' }}>
            <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.65)', fontStyle: 'italic', lineHeight: 1.7 }}>"The pens have been lifted and the pages have dried." — What has been decreed has been decreed. The garden's Observation Chain Archive is a communal reading of the Qadr. Each observation is a verse in the community's recitation of what was written.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ADAMIC NAMING FRAMING ────────────────────────────────────────────────────
function AdamicNamingFraming() {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ margin: '24px 0', borderRadius: 8, border: '1px solid rgba(45,122,71,0.25)', background: 'rgba(45,122,71,0.04)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(v => !v)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 9, color: 'rgba(45,122,71,0.8)', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace' }}>◈ Quran 2:31 · Adamic Naming — IRL Lens Layer 1 Ground</span>
        <span style={{ fontSize: 16, color: 'rgba(45,122,71,0.6)', fontFamily: 'monospace' }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ height: 1, background: 'rgba(45,122,71,0.15)', marginBottom: 4 }} />
          <div style={{ padding: '12px 16px', borderRadius: 6, background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderLeft: '3px solid rgba(45,122,71,0.5)' }}>
            <p style={{ fontSize: 13, color: 'rgba(212,197,169,0.8)', lineHeight: 1.85, fontStyle: 'italic' }}>"And He taught Adam the names — all of them." (Quran 2:31)</p>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(212,197,169,0.7)', lineHeight: 1.85 }}>When the caretaker names the thing they've encountered — "This is a fruit tree," "This is a fire hydrant," "This is a community garden" — they are exercising a capacity that the heavens and the earth and the mountains didn't possess. The angels didn't have this. You do. "He taught Adam the names — all of them."</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ padding: '12px 14px', borderRadius: 6, background: 'rgba(6,13,18,0.5)', border: '1px solid rgba(45,122,71,0.2)' }}>
              <div style={{ fontSize: 9, color: 'rgba(45,122,71,0.7)', letterSpacing: '.1em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 6 }}>Adam's Names (of Things) — Layer 1</div>
              <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.6 }}>The names of created things — fruit tree, river, community, leaf. Naming the encountered thing is the first act of Adamic inheritance. Before extracting divine Names, the caretaker names the created thing.</div>
            </div>
            <div style={{ padding: '12px 14px', borderRadius: 6, background: 'rgba(6,13,18,0.5)', border: '1px solid rgba(201,168,76,0.2)' }}>
              <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.7)', letterSpacing: '.1em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 6 }}>Allah's Names (of Attributes) — Layer 3</div>
              <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.6 }}>The Names of divine Attributes — Al-Khaliq, Al-Bari, Al-Latif, Al-Jami. Tracing the thing's names back to the Names of the One who created it. The fission process IS the bridge.</div>
            </div>
          </div>
          <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderLeft: '3px solid rgba(201,168,76,0.4)' }}>
            <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.65)', fontStyle: 'italic', lineHeight: 1.7 }}>◈ The IRL Lens bridges two naming systems: Adam's names (names of things) → Allah's names (Names of Attributes). The fission sequence IS the bridge — it takes a name-of-a-thing and decomposes it into Names-of-Allah. This is the complete Adamic function: name the thing, then recognize in the thing the Names of the One who created it.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VOCATIONAL ORIENTATION MODULES ──────────────────────────────────────────
function VocationalOrientationModules() {
  const [activeRole, setActiveRole] = React.useState<string | null>(null);
  const roles = [
    { id: 'engineer', label: 'Engineer', arabic: 'مهندس', question: 'What tools is an engineer working on, and what tools does this engineer utilize toward that purpose?', irlLens: 'Systems decomposition — the fission sequence becomes a systems analysis. What Names compose the system? What Sunnat Allah governs its operation?', amanah: 'The tools and provisions entrusted to the engineer are Mata — means toward a purpose. What purpose are they serving?', keys: 'Key 03: Taqwah — consciousness in design. Key 05: Amanah — stewardship of the systems built.', mrg: 'Infrastructure, housing, water systems — engineering as caretaking of community provisions.', color: '#2d8a87' },
    { id: 'teacher', label: 'Teacher', arabic: 'معلم', question: 'What does the teacher\'s curriculum look like?', irlLens: 'Knowledge transmission — the fission sequence becomes a pedagogical framework. How does the teacher help students recognize Names in what they study?', amanah: 'The students are an Amanah — a trust. The curriculum is a means of recognition, not just information transfer.', keys: 'Key 01: Ayat — every subject is a field of signs. Key 07: Yaqeen — moving students from Ilm to Ayn to Haqq.', mrg: 'Educational resources, literacy programs, community learning spaces — teaching as caretaking of knowledge.', color: '#c9a84c' },
    { id: 'farmer', label: 'Farmer / Gardener', arabic: 'فلاح', question: 'Which kinds of plants is the gardener planting? Where, how, why, and what Ayat is that gardener pondering while doing so?', irlLens: 'Soil analysis — the fission sequence becomes a reading of the earth. What Names are manifest in the growth cycle?', amanah: 'The land is a generational Amanah — a trust passed through lineages. Sustainable planning is the fulfillment of that trust.', keys: 'Key 04: Mizan — the balance of the ecosystem. Key 02: Haal — the state of the soil, the season, the community.', mrg: 'Food access, community gardens, urban agriculture — farming as caretaking of provision.', color: '#4a8a2d' },
    { id: 'returning-citizen', label: 'Returning Citizen', arabic: 'مصح', question: 'What reforms and resource redistribution propositions does someone that is formerly incarcerated have for existing community institutions?', irlLens: 'Restoration mapping — the fission sequence applied to community barriers becomes a map of where Islah is most needed.', amanah: 'The experience of incarceration is itself an Amanah — a trust of knowledge about the system. The returning citizen carries testimony that the community needs.', keys: 'Key 05: Amanah — the trust of lived experience. Key 04: Mizan — recognizing imbalance and working toward restoration.', mrg: 'Reentry resources, legal aid, housing, employment — restoration as caretaking of the community\'s Mizan.', color: '#7a4a9a' },
    { id: 'native', label: 'Native / Indigenous', arabic: 'أصيل', question: 'What value propositions are directing the native\'s ethical decisions regarding sustainable urban planning in their locality?', irlLens: 'Ecological lineage — the fission sequence draws from generations of observation. What Names have been recognized in this land across centuries?', amanah: 'The land is a generational Amanah — a trust passed through lineages. Sustainable planning is the fulfillment of that trust.', keys: 'Key 01: Ayat — the land as a field of signs, read across generations. Key 03: Taqwah — consciousness in relationship to the land.', mrg: 'Land rights, environmental justice, cultural preservation — indigenous stewardship as caretaking of ecological Amanah.', color: '#8a5a2d' },
    { id: 'janitor', label: 'Custodian / Janitor', arabic: 'حارس', question: 'How would a janitor approach the custodianship of endangered habitats?', irlLens: 'Custodianship as recognition — the fission sequence applied to the spaces maintained. What Names are present in the act of cleaning, preserving, maintaining?', amanah: 'The custodian is the most literal expression of Khalifah — caretaker. Every space maintained is an Amanah fulfilled.', keys: 'Key 05: Amanah — the trust of the space. Key 02: Haal — the state of the environment before and after custodianship.', mrg: 'Environmental maintenance, habitat preservation, community space stewardship — custodianship as caretaking made visible.', color: '#5a7a8a' },
  ];
  return (
    <div style={{ margin: '32px 0' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 6 }}>◈ Vocational Orientation Modules</div>
        <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.55)', lineHeight: 1.7 }}>"What if we modeled all the possible roles of our current society, redeveloping and recognizing those roles with the primary purpose being oriented toward the remembrance of Allah?" — The same IRL Lens methodology, filtered through the caretaker's specific vocation.</p>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {roles.map(role => (
          <button key={role.id} onClick={() => setActiveRole(activeRole === role.id ? null : role.id)} style={{ padding: '6px 12px', borderRadius: 5, border: `1px solid ${activeRole === role.id ? role.color : `${role.color}44`}`, background: activeRole === role.id ? `${role.color}18` : 'transparent', color: activeRole === role.id ? role.color : 'rgba(212,197,169,0.55)', fontSize: 11, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 14 }}>{role.arabic}</span>
            <span>{role.label}</span>
          </button>
        ))}
      </div>
      {activeRole && (() => {
        const role = roles.find(r => r.id === activeRole);
        if (!role) return null;
        return (
          <div style={{ borderRadius: 8, border: `1px solid ${role.color}44`, background: 'rgba(6,13,18,0.6)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${role.color}22`, background: `${role.color}0a` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: role.color }}>{role.arabic}</span>
                <span style={{ fontSize: 14, color: role.color, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase' }}>{role.label}</span>
              </div>
              <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.65)', lineHeight: 1.7, fontStyle: 'italic' }}>"{role.question}"</p>
            </div>
            <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { label: 'IRL Lens Orientation', content: role.irlLens, color: '#2d8a87' },
                { label: 'Amanah Domain', content: role.amanah, color: role.color },
                { label: 'Keys Path', content: role.keys, color: '#c9a84c' },
                { label: 'MRG Bridge', content: role.mrg, color: '#4a8a2d' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: 6, background: 'rgba(6,13,18,0.5)', border: `1px solid ${item.color}22` }}>
                  <div style={{ fontSize: 9, color: item.color, letterSpacing: '.1em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 5 }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.6 }}>{item.content}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: '10px 18px 14px', borderTop: `1px solid ${role.color}15` }}>
              <div style={{ fontSize: 10, color: `${role.color}99`, lineHeight: 1.6 }}>◈ Fisabilillah — in the way of Allah, you are entrusted with something that is running out, and you're entrusted with the means to do something in that period. What would it look like if you were mindful of Allah in your work as a {role.label.toLowerCase()}?</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── AL-QADIR ANNOTATIONS PANEL (enhanced with all 9 Names) ──────────────────
function AlQadirAnnotationsPanel({ section }: { section: LetterSection }) {
  const allNames = [
    { name: 'Al-Qadir', arabic: 'الْقَادِر', note: '3× in prose — The All-Powerful, the first Name analyzed in the letter' },
    { name: 'Al-Badi', arabic: 'الْبَدِيع', note: 'The Originator of all information — the Pen\'s first content' },
    { name: 'Al-Alim', arabic: 'الْعَلِيم', note: '3× — The All-Knowing, present in Pen hadith and Quran 2:32' },
    { name: 'Al-Khabir', arabic: 'الْخَبِير', note: 'All-Aware — paired with Al-Alim in the Pen paragraph' },
    { name: 'Al-Khaliq', arabic: 'الْخَالِق', note: 'The Creator — paired with Al-Qadir in the Taqdir paragraph' },
    { name: 'Al-Basir', arabic: 'الْبَصِير', note: 'The All-Seeing — vision and the lens as Mata' },
    { name: 'An-Nur', arabic: 'النُّور', note: 'The Light — illumination for vision, the camera sensor' },
    { name: 'Al-Hakim', arabic: 'الْحَكِيم', note: 'The All-Wise — present in Quran 2:32 closing' },
    { name: 'Al-Ali', arabic: 'الْعَلِيّ', note: '3× — The Most High, present in Quran 3:26 context' },
  ];
  return (
    <div className="letter-annotation-panel" style={{ paddingLeft: 24, minWidth: 0 }}>
      <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, padding: 16, marginBottom: 12 }}>
        <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>NAMES IN THIS SECTION</div>
        <div style={{ fontSize: 8, color: 'rgba(212,197,169,0.4)', marginBottom: 10, lineHeight: 1.5 }}>9 Names in prose · 17+ including Quranic verses</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {allNames.map((n, i) => (
            <div key={i} style={{ padding: '6px 8px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: '#c9a84c' }}>{n.arabic}</span>
                <span style={{ fontSize: 8, color: 'rgba(201,168,76,0.6)', fontFamily: 'monospace' }}>{n.name}</span>
              </div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.45)', lineHeight: 1.4 }}>{n.note}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>SECTION ARCHITECTURE</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { label: 'First Root Analysis', note: 'The first fission performed in the letter — cracking open one Name to see what\'s inside it.' },
            { label: 'Pen Hadith Anchor', note: 'The cosmological event that precedes all creation — the IRL Lens reads what the Pen wrote.' },
            { label: 'Role-Questioning Cascade', note: '18 questions reimagining every societal role through the Khalifah lens.' },
          ].map((item, i) => (
            <div key={i} style={{ padding: '6px 8px', background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.08)', borderRadius: 4 }}>
              <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.6)', marginBottom: 3, fontFamily: 'monospace', letterSpacing: '.06em' }}>{item.label}</div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.5)', lineHeight: 1.4 }}>{item.note}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, padding: 14 }}>
        <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>PLATFORM CONNECTIONS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {section.platformBridges.map((b, i) => (
            <a key={i} href={b.href} style={{ display: 'block', padding: '6px 8px', background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 4, textDecoration: 'none' }}>
              <div style={{ fontSize: 8, color: '#2d8a87', marginBottom: 2 }}>{b.label}</div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.45)', lineHeight: 1.4 }}>{b.description}</div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MOBILE ANNOTATION SHEET (collapsible, shown below content on small screens) ─
function MobileAnnotationSheet({ section }: { section: LetterSection }) {
  const [open, setOpen] = useState(false);
  const namesInSection = section.namesExtracted.map(id => LETTER_NAMES[id]).filter(Boolean);
  const hasContent = namesInSection.length > 0 || section.platformBridges.length > 0 || (section.reflectionQuestions && section.reflectionQuestions.length > 0);
  if (!hasContent) return null;
  return (
    <div className="letter-mobile-annotation-sheet">
      <button
        className="letter-mobile-annotation-toggle"
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: open ? 'rgba(201,168,76,0.08)' : 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 6, padding: '12px 16px', cursor: 'pointer', color: open ? '#c9a84c' : 'rgba(201,168,76,0.6)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase' as const, textAlign: 'left' as const, touchAction: 'manipulation', minHeight: 44 }}
      >
        <span>✦ Section Notes &amp; Connections</span>
        <span style={{ fontSize: 14, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▾</span>
      </button>
      {open && (
        <div style={{ marginTop: 8, padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {namesInSection.length > 0 && (
            <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, padding: 14 }}>
              <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>NAMES IN THIS SECTION</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {namesInSection.map((n, i) => (
                  <Link key={i} href="/names-reference" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '5px 8px', background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 4, textDecoration: 'none' }}>
                    <span style={{ fontSize: 14, color: '#c9a84c', fontFamily: "'Amiri', serif" }}>{n.arabic}</span>
                    <span style={{ fontSize: 8, color: '#7a6f5d', marginTop: 2 }}>{n.transliteration}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {section.platformBridges.length > 0 && (
            <div style={{ background: 'rgba(26,74,46,0.1)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6, padding: 14 }}>
              <div style={{ fontSize: 7, color: '#2d7a47', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>THIS SECTION GROUNDS</div>
              {section.platformBridges.map((b, i) => (
                <Link key={i} href={b.href} style={{ display: 'block', padding: '5px 0', borderBottom: i < section.platformBridges.length - 1 ? '1px solid rgba(45,122,71,0.1)' : 'none', textDecoration: 'none' }}>
                  <div style={{ fontSize: 10, color: '#2d7a47' }}>→ {b.label}</div>
                  <div style={{ fontSize: 9, color: '#7a6f5d', marginTop: 2, lineHeight: 1.4 }}>{b.description}</div>
                </Link>
              ))}
            </div>
          )}
          {section.reflectionQuestions && section.reflectionQuestions.length > 0 && (
            <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.1)', borderLeft: '3px solid rgba(201,168,76,0.4)', borderRadius: '0 6px 6px 0', padding: 14 }}>
              <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>REFLECTION QUESTIONS</div>
              {section.reflectionQuestions.map((q, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  {q.role && <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.08em', marginBottom: 3 }}>{q.role.toUpperCase()}</div>}
                  <div style={{ fontSize: 10, color: '#9a8f7d', lineHeight: 1.5 }}>{q.question}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── GOLDEN WARM SECTION (Al-Qadir — The All-Powerful) ───────────────────────
function GoldenWarmSection({ section }: { section: LetterSection }) {
  return (
    <section id={`section-${section.id}`} style={{ background: '#1a0e06', borderBottom: '1px solid rgba(201,168,76,0.1)', overflowX: 'hidden' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(32px, 5vw, 48px) clamp(12px, 4vw, 24px)', boxSizing: 'border-box', width: '100%' }}>
        {/* FIX 1: Section title renamed from "COMPASSION" to "AL-QADIR — The All-Powerful" */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 12 }}>
            {section.sectionNumber.toString().padStart(2, '0')}. AL-QADIR — The All-Powerful
          </div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(28px, 8vw, 64px)', color: '#c9a84c', lineHeight: 1, marginBottom: 8, textShadow: '0 0 40px rgba(201,168,76,0.3)', wordBreak: 'break-word' }}>
            {section.arabicTitle}
          </div>
          {section.arabicRoot && (
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(18px, 4vw, 32px)', color: 'rgba(201,168,76,0.3)', letterSpacing: 12, marginBottom: 8, wordBreak: 'break-word' }}>
              {section.arabicRoot}
            </div>
          )}
          {section.rootMeanings && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
              {section.rootMeanings.map((m, i) => (
                <span key={i} style={{ fontSize: 11, color: '#9a8f7d', background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3, padding: '3px 8px' }}>{m}</span>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 0, minWidth: 0 }} className="letter-section-grid">
          <div style={{ paddingRight: 40, borderRight: '1px solid rgba(201,168,76,0.06)', minWidth: 0, overflowWrap: 'break-word' }}>
            {/* FIX 2: Quran 3:26 — corrected label from "SURAH AL-FATIHA — THE OPENING" to "Surah Aal-Imran — 3:26" */}
            {section.ayahBlocks?.[0] && (
              <div style={{
                border: '2px solid rgba(201,168,76,0.25)',
                borderRadius: 8,
                padding: 'clamp(14px, 3vw, 24px) clamp(14px, 3vw, 28px)',
                margin: '0 0 24px',
                textAlign: 'center',
                background: 'rgba(201,168,76,0.03)',
              }}>
                <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 12 }}>
                  Surah Aal-Imran — 3:26
                </div>
                <p style={{ fontFamily: "'Amiri', serif", fontStyle: 'italic', fontSize: 'clamp(12px, 3vw, 15px)', color: '#d4c5a9', lineHeight: 2 }}>
                  {section.ayahBlocks[0].text}
                </p>
                <div style={{ fontSize: 10, color: '#c9a84c', marginTop: 8 }}>{section.ayahBlocks[0].ref}</div>
              </div>
            )}

            {/* Body paragraphs */}
            {section.paragraphs.map((para, i) => (
              <p key={i} style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', color: '#d4c5a9', lineHeight: 1.85, marginBottom: 20, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {renderAnnotatedParagraph(para, section.annotatedPhrases)}
              </p>
            ))}

            {/* Hadith blocks */}
            {section.hadithBlocks?.map((h, i) => {
              if (h.source.includes('Muslim 8a')) {
                return <FeaturedHadithBlock key={i} {...h} />;
              }
              return <HadithBlock key={i} {...h} variant={section.variant} />;
            })}

            {/* ENHANCEMENT: IhsanColumns REMOVED — Islam/Iman/Ihsan cards belong to the Ar-Rahman/Compassion section */}

            {/* Remaining ayah blocks */}
            {section.ayahBlocks?.slice(1).map((a, i) => (
              <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
            ))}
            <MobileAnnotationSheet section={section} />
          </div>
          <AlQadirAnnotationsPanel section={section} />
        </div>
      </div>

      {/* FIX: Pull quote replaced with direct letter text — not a paraphrase */}
      <div style={{ background: 'rgba(201,168,76,0.03)', borderTop: '1px solid rgba(201,168,76,0.1)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 20px)', color: '#c9a84c', maxWidth: 700, margin: '0 auto', lineHeight: 1.5 }}>
          "Every measurement given to a thing is prescribed by the owner of the highest embodiment of that concept."
        </div>
        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 10 }}>— Al-Qadir section · Direct letter text · Huduth Sifati principle · Tawhid al-Asma wa Sifat</div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 10 }}>
          <BranchButton sourceType="letter_section" sourceRef={section.id} sourceTitle={section.sectionTitle} accentColor="rgba(201,168,76,0.5)" compact />
        </div>
      </div>
    </section>
  );
}

// ─── AR-RAHMAN SECTION (Compassion — reserved for when Ar-Rahman section is added) ─
function ArRahmanSection({ section }: { section: LetterSection }) {
  return (
    <section id={`section-${section.id}`} style={{ background: '#1a0e06', borderBottom: '1px solid rgba(201,168,76,0.1)', overflowX: 'hidden' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(32px, 5vw, 64px) clamp(12px, 4vw, 24px)', boxSizing: 'border-box', width: '100%' }}>
        {/* Section title */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 12 }}>
            {section.sectionNumber.toString().padStart(2, '0')}. COMPASSION
          </div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(28px, 8vw, 64px)', color: '#c9a84c', marginBottom: 8, lineHeight: 1.2, textShadow: '0 0 40px rgba(201,168,76,0.3)', wordBreak: 'break-word' }}>
            {section.arabicTitle}
          </div>
          {section.arabicRoot && (
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(18px, 4vw, 32px)', color: 'rgba(201,168,76,0.3)', letterSpacing: 12, marginBottom: 8, wordBreak: 'break-word' }}>
              {section.arabicRoot}
            </div>
          )}
          {section.rootMeanings && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
              {section.rootMeanings.map((m, i) => (
                <span key={i} style={{ fontSize: 11, color: '#9a8f7d', background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3, padding: '3px 8px' }}>{m}</span>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 0, minWidth: 0 }} className="letter-section-grid">
          <div style={{ paddingRight: 40, borderRight: '1px solid rgba(201,168,76,0.06)', minWidth: 0, overflowWrap: 'break-word' }}>
            {/* Al-Fatiha featured block */}
            {section.ayahBlocks?.[0] && (
              <div style={{
                border: '2px solid rgba(201,168,76,0.25)',
                borderRadius: 8,
                padding: 'clamp(14px, 3vw, 24px) clamp(14px, 3vw, 28px)',
                margin: '0 0 24px',
                textAlign: 'center',
                background: 'rgba(201,168,76,0.03)',
              }}>
                <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 12 }}>
                  SURAH AL-FATIHA — THE OPENING
                </div>
                <p style={{ fontFamily: "'Amiri', serif", fontStyle: 'italic', fontSize: 'clamp(12px, 3vw, 15px)', color: '#d4c5a9', lineHeight: 2 }}>
                  {section.ayahBlocks[0].text}
                </p>
                <div style={{ fontSize: 10, color: '#c9a84c', marginTop: 8 }}>{section.ayahBlocks[0].ref}</div>
              </div>
            )}

            {/* Body paragraphs */}
            {section.paragraphs.map((para, i) => (
              <p key={i} style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', color: '#d4c5a9', lineHeight: 1.85, marginBottom: 20, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {renderAnnotatedParagraph(para, section.annotatedPhrases)}
              </p>
            ))}

            {/* Hadith blocks */}
            {section.hadithBlocks?.map((h, i) => {
              if (h.source.includes('Muslim 8a')) {
                return <FeaturedHadithBlock key={i} {...h} />;
              }
              return <HadithBlock key={i} {...h} variant={section.variant} />;
            })}

            {/* Ihsan three columns — Islam/Iman/Ihsan cards belong here in the Ar-Rahman/Compassion section */}
            <IhsanColumns />

            {/* Remaining ayah blocks */}
            {section.ayahBlocks?.slice(1).map((a, i) => (
              <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
            ))}
            <MobileAnnotationSheet section={section} />
          </div>
          <AnnotationsPanel section={section} />
        </div>
      </div>

      <div style={{ background: 'rgba(201,168,76,0.03)', borderTop: '1px solid rgba(201,168,76,0.1)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 20px)', color: '#c9a84c', maxWidth: 700, margin: '0 auto', lineHeight: 1.5, overflowWrap: 'break-word' }}>
          "{section.keyPhrase}"
        </div>
        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 10 }}>— {section.keyPhraseNote}</div>
      </div>
    </section>
  );
}

// ─── GLOSSARY SECTION ─────────────────────────────────────────────────────────
function GlossarySection({ section }: { section: LetterSection }) {
  const terms = section.glossaryTerms || [];
  return (
    <section id={`section-${section.id}`} style={{ background: '#061008', borderBottom: '1px solid rgba(201,168,76,0.08)', overflowX: 'hidden' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(32px, 5vw, 64px) clamp(12px, 4vw, 24px)', textAlign: 'center', boxSizing: 'border-box', width: '100%' }}>
        <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 20 }}>
          {section.sectionNumber.toString().padStart(2, '0')}. {section.sectionTitle.toUpperCase()}
        </div>
        {section.arabicTitle && (
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(26px, 6vw, 48px)', color: '#c9a84c', marginBottom: 32, lineHeight: 1.2, wordBreak: 'break-word' }}>
            {section.arabicTitle}
          </div>
        )}
        {section.ayahBlocks?.map((a, i) => (
          <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
        ))}
        {section.hadithBlocks?.map((h, i) => (
          <HadithBlock key={i} {...h} variant={section.variant} />
        ))}
        {section.paragraphs.map((para, i) => (
          <p key={i} style={{ fontSize: 'clamp(14px, 3.5vw, 16px)', color: '#d4c5a9', lineHeight: 1.9, marginBottom: 24, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            {renderAnnotatedParagraph(para, section.annotatedPhrases)}
          </p>
        ))}
        {section.specialCallout && <SpecialCallout callout={section.specialCallout} variant={section.variant} />}
        {section.poem && <PoemDisplay poem={section.poem} />}
      </div>
      <div style={{ background: 'rgba(201,168,76,0.03)', borderTop: '1px solid rgba(201,168,76,0.1)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 20px)', color: '#c9a84c', maxWidth: 700, margin: '0 auto', lineHeight: 1.5, overflowWrap: 'break-word' }}>
          "{section.keyPhrase}"
        </div>
        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 10 }}>— {section.keyPhraseNote}</div>
      </div>
    </section>
  );
}

// ─── PLATFORM NAME ORIGIN CALLOUT ─────────────────────────────────────────────
function PlatformNameOriginCallout() {
  return (
    <div style={{
      background: 'rgba(201,168,76,0.08)',
      border: '2px solid rgba(201,168,76,0.4)',
      borderRadius: 12,
      padding: 'clamp(16px, 4vw, 32px) clamp(16px, 5vw, 40px)',
      margin: '40px 0',
      textAlign: 'center',
    }}>
      <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(22px, 5vw, 36px)', color: '#c9a84c', marginBottom: 12, wordBreak: 'break-word' }}>
        الحمد لله كل حال وكل شيء
      </div>
      <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(14px, 3vw, 20px)', color: '#d4c5a9', marginBottom: 8 }}>
        Alhamdulillah, kulli haal wa kulli shay
      </div>
      <div style={{ fontSize: 'clamp(12px, 3.5vw, 14px)', color: '#9a8f7d', marginBottom: 20 }}>
        All praise and thanks are due to Allah, every thing and every state.
      </div>
      <div style={{ borderTop: '1px solid rgba(201,168,76,0.2)', paddingTop: 16, marginTop: 4 }}>
        <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
          THE PLATFORM IS NAMED FROM THIS PHRASE
        </div>
        <div style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.6, maxWidth: 500, margin: '0 auto 12px' }}>
          "Keys to Kulli Haal" — Keys to Every State of Being — the platform name is derived directly from this traditional response.
        </div>
        <Link href="/keys-to-kulli-haal" style={{ fontSize: 11, color: '#2d8a87', textDecoration: 'none', border: '1px solid rgba(45,138,135,0.3)', borderRadius: 3, padding: '4px 12px' }}>
          → Keys to Kulli Haal
        </Link>
      </div>
    </div>
  );
}

// ─── IMMERSIVE SECTION (enhanced for platformNameOrigin) ──────────────────────
function ImmersiveSection({ section }: { section: LetterSection }) {
  const [glowPos, setGlowPos] = useState({ x: 50, y: 50 });
  const sectionRef = useRef<HTMLElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setGlowPos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }, []);

  return (
    <section
      id={`section-${section.id}`}
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      style={{
        background: '#061008',
        borderBottom: '1px solid rgba(201,168,76,0.08)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Cursor glow */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `radial-gradient(circle 400px at ${glowPos.x}% ${glowPos.y}%, rgba(201,168,76,0.04) 0%, transparent 70%)`,
        pointerEvents: 'none',
        transition: 'background 0.1s',
      }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(32px, 5vw, 64px) clamp(12px, 4vw, 24px)', position: 'relative', boxSizing: 'border-box', width: '100%' }}>
        {/* Section label */}
        <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 20, textAlign: 'center' }}>
          {section.sectionNumber.toString().padStart(2, '0')}. {section.sectionTitle.toUpperCase()}
        </div>

        {section.arabicTitle && (
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(20px, 5vw, 32px)', color: '#c9a84c', textAlign: 'center', marginBottom: 32, wordBreak: 'break-word' }}>
            {section.arabicTitle}
          </div>
        )}

        {/* Platform name origin callout */}
        {section.platformNameOrigin && <PlatformNameOriginCallout />}

        {/* ── CARETAKER SECTION: Quran-first annotation before paragraphs ── */}
        {section.id === 'caretaker' && (
          <QuranFirstAnnotation />
        )}

        {/* Body paragraphs */}
        {section.paragraphs.map((para, i) => (
          <p key={i} style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', color: '#d4c5a9', lineHeight: 1.85, marginBottom: 24, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            {renderAnnotatedParagraph(para, section.annotatedPhrases)}
          </p>
        ))}

        {/* ── CARETAKER SECTION: Khalifah SL term + Three Tasks ── */}
        {section.id === 'caretaker' && (
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <KhalifahSLTerm />
            <ThreeTasksCallout />
            <HandedALensEmergence />
            {/* ── Explore Further: Founder's Thread · Platform Annotations · Section 2 Demands ── */}
            <ExploreFurther label="Explore Further — Founder's Thread · Platform Annotations · Architecture Demands" accentColor="rgba(201,168,76,0.5)">
              <FounderThreadVoice
                passage="I wrote that as an introduction to the Deen (Way of life) of Islam — submission, peace, connection. However, there is no better introduction to the way of life than the Quran itself; the superlative recitation, declaration, expression."
                context="Open Letter — Caretaker, Section 2"
              />
              <PassageAnnotationsPanel />
              <FiveSectionTwoDemands />
            </ExploreFurther>
          </div>
        )}

        {/* Ayah blocks */}
        {section.ayahBlocks?.map((a, i) => (
          <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
        ))}

        {/* Poem */}
        {section.poem && <PoemDisplay poem={section.poem} />}

        {/* Special callout */}
        {section.specialCallout && <SpecialCallout callout={section.specialCallout} variant={section.variant} />}

        {/* Convergence display */}
        {section.convergenceDisplay && <ConvergenceDisplay />}

        <MobileAnnotationSheet section={section} />
      </div>

      {/* Key Phrase */}
      <div style={{ background: 'rgba(201,168,76,0.03)', borderTop: '1px solid rgba(201,168,76,0.1)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 22px)', color: '#c9a84c', maxWidth: 700, margin: '0 auto', lineHeight: 1.5 }}>
          "{section.keyPhrase}"
        </div>
        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 10 }}>— {section.keyPhraseNote}</div>
      </div>
    </section>
  );
}

// ─── SPLIT SECTION ────────────────────────────────────────────────────────────
function SplitSection({ section }: { section: LetterSection }) {
  return (
    <section id={`section-${section.id}`} style={{ background: '#061a14', borderBottom: '1px solid rgba(45,122,71,0.12)', overflowX: 'hidden' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(32px, 5vw, 64px) clamp(12px, 4vw, 24px)', boxSizing: 'border-box', width: '100%' }}>
        <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 20, textAlign: 'center' }}>
          {section.sectionNumber.toString().padStart(2, '0')}. {section.sectionTitle.toUpperCase()}
        </div>
        {section.arabicTitle && (
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(28px, 6vw, 48px)', color: '#c9a84c', textAlign: 'center', marginBottom: 32, lineHeight: 1.2, wordBreak: 'break-word' }}>
            {section.arabicTitle}
          </div>
        )}
        {section.arabicRoot && (
          <RootBlock arabicRoot={section.arabicRoot} rootLetters={section.rootLetters} rootMeanings={section.rootMeanings} variant={section.variant} />
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 0, minWidth: 0 }} className="letter-section-grid">
          <div style={{ paddingRight: 40, borderRight: '1px solid rgba(45,122,71,0.1)', minWidth: 0, overflowWrap: 'break-word' }}>
            {section.ayahBlocks?.map((a, i) => (
              <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
            ))}
            {section.hadithBlocks?.map((h, i) => (
              <HadithBlock key={i} {...h} variant={section.variant} />
            ))}
            {section.paragraphs.map((para, i) => (
              <p key={i} style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', color: '#d4c5a9', lineHeight: 1.85, marginBottom: 20, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {renderAnnotatedParagraph(para, section.annotatedPhrases)}
              </p>
            ))}
            {section.specialCallout && <SpecialCallout callout={section.specialCallout} variant={section.variant} />}
            {section.poem && <PoemDisplay poem={section.poem} />}

            {/* ── THE MURAL: Ecological creation panels — biome rings, watershed, grasses ── */}
            {section.id === 'the-mural' && (
              <div style={{ marginTop: 32 }}>
                {/* Full Interactive Mural — Forest Watershed Descent with all biome rings */}
                <ExploreFurther label="Explore the Mural — Forest Watershed · Biome Rings · Ecological Creations" accentColor="rgba(45,122,71,0.7)" defaultOpen={true}>
                  <FullInteractiveMural />
                </ExploreFurther>

                {/* Garment Weaving — strand caught, stretched, threaded */}
                <div style={{ marginTop: 24 }}>
                  <ExploreFurther label="Explore Further — The Garment · Strand Caught, Stretched, Threaded" accentColor="rgba(201,168,76,0.5)">
                    <GarmentWeavingProcess
                      observationText="A fleeting thought, like a strand caught between mental fingertips, stretched out to be examined, threaded into an expression."
                      namesExtracted={['Al-Khaliq', 'Al-Musawwir', 'Ar-Razzaq']}
                    />
                  </ExploreFurther>
                </div>

                {/* Four-Beat Lens Quick Mode — daily field practice */}
                <div style={{ marginTop: 16 }}>
                  <ExploreFurther label="Explore Further — Four-Beat Lens · Daily Field Practice" accentColor="rgba(45,138,135,0.5)">
                    <FourBeatLensQuickMode />
                  </ExploreFurther>
                </div>

                {/* Observation Chain Tapestry — connected observations across cities */}
                <div style={{ marginTop: 16 }}>
                  <ExploreFurther label="Explore Further — Observation Chain Tapestry · Connections Across Cities" accentColor="rgba(138,138,184,0.5)">
                    <ObservationChainTapestry />
                  </ExploreFurther>
                </div>

                {/* Uboor Return Threshold — 36:83 crossing-over moment */}
                <div style={{ marginTop: 16 }}>
                  <ExploreFurther label="Explore Further — Uboor Return Threshold · 36:83" accentColor="rgba(201,168,76,0.4)">
                    <UboorReturnThreshold />
                  </ExploreFurther>
                </div>
              </div>
            )}

            <MobileAnnotationSheet section={section} />
          </div>
          <AnnotationsPanel section={section} />
        </div>
      </div>
      <div style={{ background: 'rgba(201,168,76,0.03)', borderTop: '1px solid rgba(201,168,76,0.1)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 20px)', color: '#c9a84c', maxWidth: 700, margin: '0 auto', lineHeight: 1.5 }}>
          "{section.keyPhrase}"
        </div>
        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 10 }}>— {section.keyPhraseNote}</div>
      </div>
    </section>
  );
}

// ─── CENTERED SECTION ─────────────────────────────────────────────────────────
function CenteredSection({ section }: { section: LetterSection }) {
  return (
    <section id={`section-${section.id}`} style={{ background: '#f5f0e8', borderBottom: '1px solid rgba(139,100,20,0.15)', overflowX: 'hidden' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'clamp(32px, 5vw, 64px) clamp(12px, 4vw, 24px)', boxSizing: 'border-box', width: '100%' }}>
        <div style={{ fontSize: 9, color: 'rgba(139,100,20,0.7)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 20, textAlign: 'center' }}>
          {section.sectionNumber.toString().padStart(2, '0')}. {section.sectionTitle.toUpperCase()}
        </div>
        {section.arabicTitle && (
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(26px, 6vw, 48px)', color: 'rgba(139,100,20,0.8)', textAlign: 'center', marginBottom: 32, lineHeight: 1.2, wordBreak: 'break-word' }}>
            {section.arabicTitle}
          </div>
        )}
        {section.arabicRoot && (
          <RootBlock arabicRoot={section.arabicRoot} rootLetters={section.rootLetters} rootMeanings={section.rootMeanings} variant={section.variant} />
        )}
        {section.ayahBlocks?.map((a, i) => (
          <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
        ))}
        {section.hadithBlocks?.map((h, i) => (
          <HadithBlock key={i} {...h} variant={section.variant} />
        ))}
        {section.paragraphs.map((para, i) => (
          <p key={i} style={{ fontSize: 'clamp(14px, 3.5vw, 16px)', color: '#1a1a0e', lineHeight: 1.9, marginBottom: 24, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            {renderAnnotatedParagraph(para, section.annotatedPhrases)}
          </p>
        ))}
        {section.specialCallout && <SpecialCallout callout={section.specialCallout} variant={section.variant} />}
        {section.poem && <PoemDisplay poem={section.poem} />}
      </div>
      <div style={{ background: 'rgba(139,100,20,0.06)', borderTop: '1px solid rgba(139,100,20,0.15)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 20px)', color: 'rgba(139,100,20,0.9)', maxWidth: 700, margin: '0 auto', lineHeight: 1.5 }}>
          "{section.keyPhrase}"
        </div>
        <div style={{ fontSize: 11, color: 'rgba(139,100,20,0.5)', marginTop: 10 }}>— {section.keyPhraseNote}</div>
      </div>
    </section>
  );
}

// ─── DARK SECTION ─────────────────────────────────────────────────────────────
function DarkSection({ section }: { section: LetterSection }) {
  return (
    <section id={`section-${section.id}`} style={{ background: '#060d06', borderBottom: '1px solid rgba(201,168,76,0.08)', overflowX: 'hidden' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(32px, 5vw, 64px) clamp(12px, 4vw, 24px)', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 20, textAlign: 'center' }}>
          {section.sectionNumber.toString().padStart(2, '0')}. {section.sectionTitle.toUpperCase()}
        </div>
        {section.arabicTitle && (
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(26px, 7vw, 56px)', color: '#c9a84c', textAlign: 'center', marginBottom: 32, lineHeight: 1.2, textShadow: '0 0 60px rgba(201,168,76,0.2)', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {section.arabicTitle}
          </div>
        )}
        {section.arabicRoot && (
          <RootBlock arabicRoot={section.arabicRoot} rootLetters={section.rootLetters} rootMeanings={section.rootMeanings} variant={section.variant} />
        )}
        {section.ayahBlocks?.map((a, i) => (
          <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
        ))}
        {section.hadithBlocks?.map((h, i) => (
          <HadithBlock key={i} {...h} variant={section.variant} />
        ))}
        {section.paragraphs.map((para, i) => (
          <p key={i} style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', color: '#d4c5a9', lineHeight: 1.85, marginBottom: 20, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            {renderAnnotatedParagraph(para, section.annotatedPhrases)}
          </p>
        ))}
        {section.specialCallout && <SpecialCallout callout={section.specialCallout} variant={section.variant} />}
        {section.poem && <PoemDisplay poem={section.poem} />}
      </div>
      <div style={{ background: 'rgba(201,168,76,0.03)', borderTop: '1px solid rgba(201,168,76,0.1)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 20px)', color: '#c9a84c', maxWidth: 700, margin: '0 auto', lineHeight: 1.5, overflowWrap: 'break-word' }}>
          "{section.keyPhrase}"
        </div>
        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 10 }}>— {section.keyPhraseNote}</div>
      </div>
    </section>
  );
}

// ─── QUOTE SECTION ────────────────────────────────────────────────────────────
function QuoteSection({ section }: { section: LetterSection }) {
  return (
    <section id={`section-${section.id}`} style={{ background: '#060d12', borderBottom: '1px solid rgba(201,168,76,0.08)', overflowX: 'hidden' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 'clamp(32px, 5vw, 64px) clamp(12px, 4vw, 24px)', textAlign: 'center', boxSizing: 'border-box', width: '100%' }}>
        <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 20 }}>
          {section.sectionNumber.toString().padStart(2, '0')}. {section.sectionTitle.toUpperCase()}
        </div>
        {section.arabicTitle && (
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(26px, 6vw, 48px)', color: '#c9a84c', marginBottom: 32, lineHeight: 1.2, wordBreak: 'break-word' }}>
            {section.arabicTitle}
          </div>
        )}
        {section.ayahBlocks?.map((a, i) => (
          <AyahBlock key={i} ref={a.ref} text={a.text} variant={section.variant} />
        ))}
        {section.hadithBlocks?.map((h, i) => (
          <HadithBlock key={i} {...h} variant={section.variant} />
        ))}
        {section.paragraphs.map((para, i) => (
          <p key={i} style={{ fontSize: 'clamp(14px, 3.5vw, 16px)', color: '#d4c5a9', lineHeight: 1.9, marginBottom: 24, fontFamily: "'Georgia', serif", fontStyle: 'italic', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            {renderAnnotatedParagraph(para, section.annotatedPhrases)}
          </p>
        ))}
        {section.specialCallout && <SpecialCallout callout={section.specialCallout} variant={section.variant} />}
        {section.poem && <PoemDisplay poem={section.poem} />}
      </div>
      <div style={{ background: 'rgba(201,168,76,0.03)', borderTop: '1px solid rgba(201,168,76,0.1)', padding: 'clamp(16px, 4vw, 32px) clamp(12px, 4vw, 24px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 20px)', color: '#c9a84c', maxWidth: 700, margin: '0 auto', lineHeight: 1.5, overflowWrap: 'break-word' }}>
          "{section.keyPhrase}"
        </div>
        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 10 }}>— {section.keyPhraseNote}</div>
      </div>
    </section>
  );
}

// ─── SECTION ROUTER ───────────────────────────────────────────────────────────
function LetterSection({ section }: { section: LetterSection }) {
  switch (section.variant) {
    case 'immersive': return <ImmersiveSection section={section} />;
    case 'golden-warm': return <GoldenWarmSection section={section} />;
    case 'terminal': return <TerminalSection section={section} />;
    case 'paired-names': return <PairedNamesSection section={section} />;
    case 'glossary': return <GlossarySection section={section} />;
    case 'split': return <SplitSection section={section} />;
    case 'centered': return <CenteredSection section={section} />;
    case 'dark': return <DarkSection section={section} />;
    case 'quote': return <QuoteSection section={section} />;
    default: return <ImmersiveSection section={section} />;
  }
}

// ─── NEW PLATFORM ENHANCEMENTS (curated, forward-looking) ────────────────────
const NEW_PLATFORM_ENHANCEMENTS = [
  // Search & Discovery
  { category: 'Search', text: 'Global Search: Add "Observation Chain" as a searchable content type — find narrative sequences by city, domain, or Name of Allah', color: '#2d8a87' },
  { category: 'Search', text: 'Global Search: Locality-aware search — surface resources, observations, and phases relevant to the caretaker\'s city first', color: '#2d8a87' },
  { category: 'Search', text: 'Observation Chain: "Caretaker Contributions" — allow multiple caretakers to add observations to the same chain from different localities', color: '#2d8a87' },
  { category: 'Observation Chain', text: 'Observation Chain: Export as illustrated garment — generate a visual garment pattern from a completed observation chain', color: '#2d8a87' },
  { category: 'Observation Chain', text: 'Observation Chain: Quest integration — chains automatically fulfill "Observation Chain" quest milestones when 3+ observations are linked', color: '#2d8a87' },
  { category: 'IRL Lens', text: 'IRL Lens: "Haal Snapshot" — capture the full state of a moment (zahir + batin + Name + Ayat + vantage point) in one tap', color: '#2d8a87' },
  { category: 'IRL Lens', text: 'IRL Lens: Offline observation logging — log observations without connectivity, sync when online', color: '#2d8a87' },
  // Toasts & Notifications
  { category: 'Notifications', text: 'Notifications: "Alhamdulilah Milestone" toast — celebrate when a caretaker crosses a new recognition level with a contextual Quranic verse', color: '#c9a84c' },
  { category: 'Notifications', text: 'Notifications: Weekly digest push notification — "Your caretaker cohort completed 12 observations this week"', color: '#c9a84c' },
  { category: 'Notifications', text: 'Notifications: Batin Depth Unlock ceremony — full-screen reveal animation when a new hidden layer is unlocked', color: '#c9a84c' },
  // Community & Social
  { category: 'Community', text: 'Community: "Garment Archive" city view — browse shared garment patterns organized by city ward and caretaking domain on a map', color: '#8a8ab8' },
  { category: 'Community', text: 'Community: Cohort Quest leaderboard — show combined alhamdulilah progress for role-based cohorts racing toward a shared goal', color: '#8a8ab8' },
  { category: 'Community', text: 'Community: "Fisabilillah Feed" algorithm — surface acts of service first, deprioritize passive content sharing', color: '#8a8ab8' },
  { category: 'Community', text: 'Community: Caretaker Spotlight nomination — allow community members to nominate a caretaker for weekly spotlight', color: '#8a8ab8' },
  // Dashboard & Personalization
  { category: 'Dashboard', text: 'Dashboard: "Observation Chain" widget — show the caretaker\'s most recent chain with a one-tap "Add Observation" button', color: '#D4A82A' },
  { category: 'Dashboard', text: 'Dashboard: Role-adaptive layout — dashboard widgets reorder themselves based on the caretaker\'s most-used modules', color: '#D4A82A' },
  { category: 'Dashboard', text: 'Dashboard: "Haal State" ambient indicator — a subtle color shift in the dashboard background reflecting the caretaker\'s current recognition level', color: '#D4A82A' },
  // Names Reference
  { category: 'Names', text: 'Names Reference: "Observation Count" badge — show how many IRL observations have been linked to each Name across all caretakers', color: '#8a8ab8' },
  { category: 'Names', text: 'Names Reference: "Sunnat Sequence Builder" — allow caretakers to compose their own Name sequences and save as personal timelines', color: '#8a8ab8' },
  { category: 'Names', text: 'Names Reference: Cross-link Names to Open Letter sections where they appear — "This Name appears in Section 2, 19, and 31"', color: '#8a8ab8' },
  // MRG & Resources
  { category: 'MRG', text: 'MRG: Ward-level resource density map — visualize which wards have the most caretaker activity and resource coverage', color: '#2d7a47' },
  { category: 'MRG', text: 'MRG: "Caretaker Verified" badge — allow caretakers to verify that a resource is still active and accessible in their locality', color: '#2d7a47' },
  { category: 'MRG', text: 'MRG: Resource request form — caretakers can submit new resources for review and addition to the guide', color: '#2d7a47' },
  // Platform Architecture
  { category: 'Platform', text: 'Platform: "Kaif Haal?" greeting system — personalized greeting based on the caretaker\'s current level and recent activity', color: '#7a6f5d' },
  { category: 'Platform', text: 'Platform: High-contrast mode — a high-contrast version of the dark theme for low-vision caretakers', color: '#7a6f5d' },
  { category: 'Platform', text: 'Platform: Accessibility', color: '#7a6f5d' },
  { category: 'Platform', text: 'Platform: Offline', text: 'Platform: Progressive Web App (PWA) — install the platform as a home screen app with offline quest logging', color: '#7a6f5d' },
];

// ─── LETTER BRIDGES ───────────────────────────────────────────────────────────
export function LetterBridges() {
  return (
    <section style={{ padding: 'clamp(40px, 8vw, 80px) clamp(16px, 5vw, 48px)', background: '#060d12' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Guided Path Trail — letter sections → platform modules */}
        <GuidedPathTrail />
      </div>
    </section>
  );
}

// ─── LETTER BODY ──────────────────────────────────────────────────────────────
export function LetterBody() {
  return (
    <div>
      {LETTER_SECTIONS.map(section => (
        <LetterSection key={section.id} section={section} />
      ))}
    </div>
  );
}